"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Matrix = exports.MatrixSerializer = exports.MatrixDeserializers = void 0;
exports.MatrixDeserializers = {
    json: v => {
        try {
            return JSON.parse(v);
        }
        catch (e) {
            return v;
        }
    },
    number: v => Number(v),
    string: v => v,
};
exports.MatrixSerializer = {
    number: (v) => String(v),
    string: (v) => v,
    json: v => {
        if (typeof v === 'number' || typeof v === 'string')
            return v;
        try {
            return JSON.parse(v);
        }
        catch (e) {
            return v;
        }
    },
};
const defaultSerializeOptions = {
    headerDelimiter: ',',
    metaDelimiter: ';',
    delimiter: '',
    deserializer: exports.MatrixDeserializers.json,
    serializer: exports.MatrixSerializer.json,
};
const HEIGHTS = 'x0123456789abcdefghijklmnopqrstuvwyz';
class Matrix {
    constructor(width = 3, height = width, data = new Array(width * height).fill(null), meta = []) {
        this.width = width;
        this.height = height;
        this.data = data;
        this.meta = meta;
        this.data = data.slice(0, width * height);
    }
    *rowsEntries(start = 0, end = this.height - 1) {
        for (let i = start; i <= end; i++) {
            yield [i, this.getRow(i)];
        }
    }
    *columnsEntries() {
        for (let i = 0; i < this.width; i++) {
            yield [i, this.getCol(i)];
        }
    }
    /**
     * Retorna o índice pela linha e coluna
     * @param x Coluna
     * @param y Linha
     */
    getIndexOf(x, y) {
        return y * this.width + x;
    }
    /**
     * Retorna a linha e coluna pelo índice
     * @param index Índice
     */
    getCoords(index) {
        return {
            x: index % this.width,
            y: Math.floor(index / this.width),
        };
    }
    /**
     * Retorna um valor pela linha e coluna
     * @param x Coluna
     * @param y Linha
     * @param defaultValue Valor padrão caso não seja definido
     */
    get(x, y, defaultValue = null) {
        if (x < 0 || y < 0 || x >= this.width || y >= this.height)
            return defaultValue;
        const index = this.getIndexOf(x, y);
        return index >= this.data.length ? defaultValue : this.data[index];
    }
    /**
     * Define um valor pela linha e coluna
     * @param x Coluna
     * @param y Linha
     * @param value Novo valor
     */
    set(x, y, value) {
        const index = this.getIndexOf(x, y);
        this.data[index] = value;
        return this;
    }
    /**
     * Retorna uma coluna inteira
     * @param x Índice da coluna
     */
    getCol(x) {
        if (x >= this.width)
            throw new Error(`Invalid column!`);
        return this.data.filter((_, index) => {
            return (index - x) % this.width === 0;
        });
    }
    /**
     * Retorna uma linha inteira
     * @param y Índice da linha
     */
    getRow(y) {
        if (y >= this.height)
            throw new Error(`Invalid row!`);
        const start = y * this.width;
        const end = start + this.width;
        return this.data.slice(start, end);
    }
    /**
     * Preenche a matrix com um valor
     * @param value valor a ser preenchido
     */
    fill(value) {
        if (typeof value === 'function') {
            this.data = this.map(value).data;
        }
        else {
            this.data = new Array(this.width * this.height).fill(value);
        }
        return this;
    }
    /**
     * Retorna uma string única contendo a matriz
     */
    toLegacyString() {
        return this.mapRows((lin, nlin) => lin.map((val) => HEIGHTS[val]).join('')).join('\n');
    }
    /**
     * Mapeia todas as linhas da matriz
     * @param cb Callback de mapeamento
     */
    mapRows(cb) {
        const maped = [];
        for (let y = 0; y < this.height; y++) {
            maped[y] = cb(this.getRow(y), y, this);
        }
        return maped;
    }
    /**
     * Mapeia todas as colunas da matriz
     * @param cb Callback de mapeamento
     */
    mapCols(cb) {
        const maped = [];
        for (let x = 0; x < this.width; x++) {
            maped[x] = cb(this.getCol(x), x, this);
        }
        return maped;
    }
    /**
     * Mapeia todos os itens da matriz
     * @param cb Callback de mapeamento
     */
    map(cb) {
        return Matrix.fromArray(this.data.map((value, index) => {
            const { x, y } = this.getCoords(index);
            return cb(value, x, y, this);
        }), this.width, this.height);
    }
    /**
     * Cria uma nova matriz apartir desta
     */
    clone() {
        return Matrix.from(this.data, this.width, this.height);
    }
    /**
     * Retorna se todos os elementos passaram em um teste
     * @param cb Callback de teste
     */
    every(cb) {
        return this.data.every((value, index) => {
            const { x, y } = this.getCoords(index);
            return cb(value, x, y, this);
        });
    }
    /**
     * Retorna se alguns elementos passaram em um teste
     * @param cb Callback de teste
     */
    some(cb) {
        return this.data.some((value, index) => {
            const { x, y } = this.getCoords(index);
            return cb(value, x, y, this);
        });
    }
    /**
     * Retorna se algumas linhas passaram em um teste
     * @param cb Callback de teste
     */
    someRows(cb) {
        for (let i = 0; i < this.height; i++) {
            if (cb(this.getRow(i), i, this))
                return true;
        }
        return false;
    }
    /**
     * Retorna se algumas colunas passaram em um teste
     * @param cb Callback de teste
     */
    someColuns(cb) {
        for (let i = 0; i < this.width; i++) {
            if (cb(this.getCol(i), i, this))
                return true;
        }
        return false;
    }
    /**
     * Executa uma função para cada elemento
     * @param cb Callback a ser executado para cada item
     */
    forEach(cb) {
        return this.data.forEach((value, index) => {
            const { x, y } = this.getCoords(index);
            return cb(value, x, y, this);
        });
    }
    *entries() {
        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width; x++) {
                yield [[x, y], this.get(x, y)];
            }
        }
    }
    /**
     * Executa uma função para cada linha
     * @param cb Callback a ser executado para cada linha
     */
    forEachRow(cb) {
        for (let y = 0; y < this.height; y++) {
            cb(this.getRow(y), +y, this);
        }
    }
    /**
     * Reduz o valor do array em outro valor
     * @param cb Callback de redução
     * @param initialValue Valor inicião
     */
    reduce(cb, initialValue) {
        return this.data.reduce((acc, value, index) => {
            const { x, y } = this.getCoords(index);
            return cb(acc, value, x, y, this);
        }, initialValue);
    }
    /**
     * Retorna os valores vizinhos à outro elemento pela linha e coluna
     * @param x Coluna
     * @param y Linha
     */
    neighborsOf(x, y, items = Matrix.NEIGHBORS_ALL) {
        return items.map(r => this.get(x + r.x, y + r.y));
    }
    /**
     * Cria uma nova matriz apartir de uma string codificada
     * @param encodedMatrix String codificada
     * @param param1 Opções de decodificação
     */
    static parse(encodedMatrix, { metaDelimiter = defaultSerializeOptions.metaDelimiter, headerDelimiter = defaultSerializeOptions.headerDelimiter, deserializer = defaultSerializeOptions.deserializer, delimiter = defaultSerializeOptions.delimiter, } = {}) {
        const [encodedHeader, encodedData] = encodedMatrix.split(metaDelimiter);
        const [cols, rows, ...meta] = encodedHeader.split(headerDelimiter);
        deserializer = deserializer || exports.MatrixDeserializers.string;
        const data = encodedData.split(delimiter).map(deserializer);
        return this.fromArray(data, +cols, +rows, meta.map(exports.MatrixDeserializers.json));
    }
    /**
     * Cria uma nova matrix a partir de um Array
     * @param data Array
     * @param [cols] Quantidade de colunas
     * @param [rows] Quantidade de linhas
     * @param [meta] Informações customizadas
     */
    static fromArray(data, cols, rows, meta = []) {
        return new Matrix(cols, rows, data, meta);
    }
    static from(data, cols, rows, options) {
        // Clone
        if (data instanceof this)
            return data.clone();
        // Encoded String
        if (typeof data === 'string') {
            options = cols || defaultSerializeOptions;
            return this.parse(data, options);
        }
        // Array
        if (Array.isArray(data)) {
            // 2D Array
            if (Array.isArray(data[0])) {
                cols = data[0].length;
                rows = data.length;
                return this.fromArray([].concat(...data), cols, rows);
            }
            // 1D Array
            return new Matrix(cols, rows, data);
        }
        throw new TypeError(`Type of data should be Array, Array2D, String or Matrix instance.`);
    }
    /**
     * Codifica a matriz em uma string
     * @param param0 Opções de codificação
     */
    stringify({ headerDelimiter = ',', metaDelimiter = ':', delimiter = ',', serializer = v => v } = {}) {
        return [
            [this.width, this.height, ...this.meta.map(exports.MatrixSerializer.json)].join(headerDelimiter),
            this.data.map(serializer).join(delimiter),
        ].join(metaDelimiter);
    }
    toString() {
        return `[Matrix ${this.width}x${this.height}] {\n  ${this.mapRows(v => v.map(v => String(v || 0)).join(' ')).join('\n  ')}\n}`;
    }
    static fromLegacyString(map) {
        map = map.replace(new RegExp(`[^${HEIGHTS}\n]|^\n+|\n+$`, 'gm'), '');
        const { data, cols, rows } = map.split('').reduce((acc, char) => {
            if (char === '\n') {
                acc.rowCol = 0;
                acc.rows++;
            }
            else {
                const i = HEIGHTS.indexOf(char);
                acc.data.push(i);
                acc.cols = Math.max(acc.cols, ++acc.rowCol);
                acc.rows = Math.max(1, acc.rows);
            }
            return acc;
        }, {
            data: [],
            cols: 0,
            rowCol: 0,
            rows: 0,
        });
        return Matrix.from(data, cols, rows);
    }
}
exports.Matrix = Matrix;
Matrix.NEIGHBORS = {
    TOP_LEFT: { x: -1, y: -1 },
    TOP: { x: 0, y: -1 },
    TOP_RIGHT: { x: 1, y: -1 },
    RIGHT: { x: 1, y: 0 },
    BOTTOM_RIGHT: { x: 1, y: 1 },
    BOTTOM: { x: 0, y: 1 },
    BOTTOM_LEFT: { x: -1, y: 1 },
    LEFT: { x: -1, y: 0 },
    CENTER: { x: 0, y: 0 },
};
Matrix.NEIGHBORS_ALL = [
    Matrix.NEIGHBORS.TOP_LEFT,
    Matrix.NEIGHBORS.TOP,
    Matrix.NEIGHBORS.TOP_RIGHT,
    Matrix.NEIGHBORS.RIGHT,
    Matrix.NEIGHBORS.BOTTOM_RIGHT,
    Matrix.NEIGHBORS.BOTTOM,
    Matrix.NEIGHBORS.BOTTOM_LEFT,
    Matrix.NEIGHBORS.LEFT,
];
Matrix.NEIGHBORS_ADJACENT = [
    Matrix.NEIGHBORS.TOP,
    Matrix.NEIGHBORS.RIGHT,
    Matrix.NEIGHBORS.BOTTOM,
    Matrix.NEIGHBORS.LEFT,
];
Matrix.NEIGHBORS_DIAGONAL = [
    Matrix.NEIGHBORS.TOP_LEFT,
    Matrix.NEIGHBORS.TOP_RIGHT,
    Matrix.NEIGHBORS.BOTTOM_RIGHT,
    Matrix.NEIGHBORS.BOTTOM_LEFT,
];
